import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

const AProposHero = () => (
  <section className="relative h-[420px] md:h-[520px] flex items-end overflow-hidden">
    <img
      src="https://readdy.ai/api/search-image?query=elegant%20luxury%20interior%20design%20studio%20workspace%20Abidjan%20Africa%20warm%20golden%20light%20creative%20atelier%20designer%20desk%20moodboard%20fabric%20samples%20sophisticated%20atmosphere&width=1920&height=520&seq=evco_apropos_hero001&orientation=landscape"
      alt="À propos Ev & Co Design"
      className="absolute inset-0 w-full h-full object-cover object-top"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-brand-anthracite/80 via-brand-anthracite/30 to-transparent" />
    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-14">
      <div className="inline-flex items-center gap-2 mb-3">
        <span className="h-px w-6 bg-brand-gold" />
        <span className="text-brand-gold text-xs font-medium tracking-[0.2em] uppercase">À propos</span>
      </div>
      <h1 className="font-serif text-4xl lg:text-6xl text-white leading-tight">
        Mon histoire,<br /><em>ma passion</em>
      </h1>
    </div>
  </section>
);

const AProposContent = () => {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation();

  return (
    <section className="bg-brand-beige py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left – Text */}
          <div
            ref={leftRef}
            className={`transition-all duration-800 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
            style={{ transitionDuration: '800ms' }}
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="h-px w-6 bg-brand-coral" />
              <span className="text-brand-coral text-xs font-medium tracking-[0.2em] uppercase">Mon parcours</span>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-brand-anthracite leading-tight mb-8">
              Ashley Eyoum,<br />
              <em className="text-brand-fuchsia">Fondatrice & Directrice Créative</em>
            </h2>

            {/* Story */}
            <div className="space-y-5 mb-10">
              <p className="text-brand-gray leading-relaxed">
                Je suis <strong className="text-brand-anthracite">Ashley Eyoum</strong>, trentenaire, maman de deux enfants et épouse. Mon parcours est atypique : diplômée en management des achats, j'ai commencé ma carrière dans le secteur des achats, d'abord au Cameroun, puis en France, et tout récemment en Côte d'Ivoire.
              </p>
              <p className="text-brand-gray leading-relaxed">
                Parallèlement à ma carrière, je cultive depuis <strong className="text-brand-anthracite">plus de huit ans</strong> une passion pour la décoration et le design. Autodidacte au départ, j'ai progressivement enrichi mon savoir-faire en m'inspirant des meilleures pratiques et en collaborant avec des professionnels, afin de proposer des prestations de qualité et de valeur ajoutée, qui reflètent un véritable sens de l'esthétique et du détail.
              </p>
            </div>

            {/* Mission & Vision */}
            <div className="space-y-5 mb-10">
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 shrink-0 rounded-full bg-brand-fuchsia/10 flex items-center justify-center">
                  <i className="ri-focus-3-line text-brand-fuchsia text-lg" />
                </div>
                <div>
                  <h4 className="text-brand-anthracite font-semibold mb-1">Ma mission</h4>
                  <p className="text-brand-gray text-sm leading-relaxed">
                    Apporter aux particuliers et aux entreprises un service de décoration haut de gamme, qui allie créativité, élégance et professionnalisme, tout en rendant chaque projet unique et fidèle à l'identité de mes clients.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 shrink-0 rounded-full bg-brand-coral/10 flex items-center justify-center">
                  <i className="ri-eye-line text-brand-coral text-lg" />
                </div>
                <div>
                  <h4 className="text-brand-anthracite font-semibold mb-1">Ma vision</h4>
                  <p className="text-brand-gray text-sm leading-relaxed">
                    Rehausser le standard de la décoration en Côte d'Ivoire, en mettant à disposition des services de qualité souvent rencontrés en Occident, mais rares sur le marché local. Chaque projet est pensé pour que le client se reconnaisse pleinement dans l'espace créé.
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-brand-gold/30">
              {[
                { number: '6+', label: 'Ans de passion' },
                { number: '70+', label: 'Projets réalisés' },
                { number: '98%', label: 'Clients satisfaits' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="font-serif text-3xl font-bold text-brand-fuchsia mb-1">{stat.number}</div>
                  <div className="text-xs text-brand-gray">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right – Image + Target cards */}
          <div
            ref={rightRef}
            className={`transition-all duration-800 ${rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
            style={{ transitionDuration: '800ms' }}
          >
            <div className="relative mb-10">
              <div className="w-full h-[580px] rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/c321e4bf9ecc5a3367262a0d492ee2cc.jpeg"
                  alt="Ashley Eyoum - Fondatrice Ev & Co Design"
                  className="w-full h-full object-cover object-top scale-90"
                />
              </div>

              <div
                className="absolute -bottom-6 -left-6 max-w-xs p-5 rounded-2xl"
                style={{ background: 'linear-gradient(135deg, #e63e8b, #f78b49)' }}
              >
                <i className="ri-double-quotes-l text-white/40 text-2xl mb-2 block" />
                <p className="text-white text-sm italic leading-relaxed mb-3">
                  Chaque projet est une opportunité de créer quelque chose d'unique et d'inoubliable.
                </p>
                <div>
                  <p className="text-white font-semibold text-sm">Ashley Eyoum</p>
                  <p className="text-white/75 text-xs">Fondatrice &amp; Directrice Créative</p>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 w-14 h-14 rounded-full border-2 border-brand-gold/40" />
            </div>

            {/* Ma cible */}
            <div className="mt-10 pt-8">
              <h4 className="text-brand-anthracite font-serif text-xl font-semibold mb-5">
                Ma cible — <em className="text-brand-coral">Vous êtes au bon endroit</em>
              </h4>
              <div className="space-y-4">
                <div className="flex gap-4 items-start bg-white rounded-xl p-4">
                  <div className="w-9 h-9 shrink-0 rounded-full bg-brand-fuchsia/10 flex items-center justify-center">
                    <i className="ri-building-2-line text-brand-fuchsia" />
                  </div>
                  <div>
                    <p className="text-brand-anthracite font-medium text-sm mb-1">Les entreprises</p>
                    <p className="text-brand-gray text-xs leading-relaxed">
                      Vous souhaitez créer des espaces innovants et inspirants pour vos collaborateurs ou vos clients — nous concevons des environnements à votre image.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start bg-white rounded-xl p-4">
                  <div className="w-9 h-9 shrink-0 rounded-full bg-brand-coral/10 flex items-center justify-center">
                    <i className="ri-user-heart-line text-brand-coral" />
                  </div>
                  <div>
                    <p className="text-brand-anthracite font-medium text-sm mb-1">Les particuliers</p>
                    <p className="text-brand-gray text-xs leading-relaxed">
                      Vous recherchez des prestations haut de gamme, valorisez la qualité et souhaitez être accompagnés avec rigueur et sens du détail.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const AProposCta = () => (
  <section
    className="py-20 px-6 text-center"
    style={{ background: 'linear-gradient(135deg, #f78b49, #e63e8b)' }}
  >
    <div className="max-w-2xl mx-auto">
      <h2 className="font-serif text-3xl lg:text-4xl text-white mb-4">
        Envie de travailler ensemble ?
      </h2>
      <p className="text-white/80 mb-8 leading-relaxed">
        Découvrez nos services ou contactez-nous directement pour discuter de votre projet.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-8 py-4 bg-white rounded-full text-brand-fuchsia font-medium text-sm whitespace-nowrap transition-all duration-300 hover:scale-105 cursor-pointer"
        >
          <i className="ri-mail-send-line" />
          Nous contacter
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/40 text-white font-medium text-sm whitespace-nowrap transition-all duration-300 hover:bg-white/10 cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          Retour à l'accueil
        </Link>
      </div>
    </div>
  </section>
);

const AProposPage = () => (
  <>
    <title>À propos | Ashley Eyoum — Fondatrice Ev & Co Design Abidjan</title>
    <meta name="description" content="Découvrez l'histoire d'Ashley Eyoum, fondatrice de l'agence de décoration Ev & Co Design à Abidjan. Passion, expertise et vision au service de vos projets en Côte d'Ivoire." />
    <meta name="keywords" content="Ashley Eyoum, agence décoration Abidjan, Ev Co Design fondatrice, décoration Côte d'Ivoire" />
    <link rel="canonical" href="https://evcodesign-ci.com/a-propos" />
    <meta property="og:title" content="À propos | Ashley Eyoum — Ev & Co Design Abidjan" />
    <meta property="og:description" content="L'histoire et la passion d'Ashley Eyoum, fondatrice de l'agence de décoration Ev & Co Design à Abidjan." />
    <main className="font-sans bg-brand-beige min-h-screen">
      <Navbar />
      <AProposHero />
      <AProposContent />
      <AProposCta />
      <Footer />
    </main>
  </>
);

export default AProposPage;
